import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink, useSearchParams } from 'react-router-dom'

import { Add, Delete, Edit, PlaylistAddCheck, Search } from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  TableContainer,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'

import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog'
import { GRTable, GRTableColumn } from '../../../components/GRTable/GRTable'
import { useAppDispatch } from '../../../hooks/storeHooks'
import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import { ConfirmDialogData } from '../../../types/ConfirmDialogData'
import { useGetInternalFeatureMapQuery } from '../../api/features'
import { useGetInternalMarketsQuery } from '../../api/internal-markets'
import { useGetLanguageMapsQuery } from '../../api/language'
import {
  invalidateInternalTags,
  useDeleteInternalTagMutation,
  useGetInternalConceptsQuery,
  useGetInternalTagImplementationsQuery,
  useGetInternalTagsQuery,
  useSaveInternalTagMutation,
  useSaveInternalTagOrderMutation,
} from '../../api/tags'
import { CheckedStatusCircle } from '../../components/CheckedStatusCircle/CheckedStatusCircle'
import { QueryStateBoundary } from '../../components/QueryStateBoundary/QueryStateBoundary'
import { TagAssignmentCount } from '../../features/tags/components/TagAssignmentCount/TagAssignmentCount'
import { TagEditDialog } from '../../features/tags/components/TagEditDialog/TagEditDialog'
import {
  allFeatureTags,
  buildTagRows,
  filterTagRows,
  getFeatureTags,
  getUnstoredTagFields,
  hasChoiceColumn,
  hasConceptColumns,
  hasFeatureColumn,
  hasGamesColumn,
  isAssignableTagType,
  isConceptStored,
  isTagType,
  TagField,
  tagFieldLabelKeys,
  TagRow,
  tagTypeLabelKeys,
  tagTypes,
  toActiveToggleBody,
  toTagOrder,
} from '../../features/tags/components/helpers/tagHelpers'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'
import { InternalTag, InternalTagSaveBody, InternalTagType } from '../../types/InternalTag'

const defaultMarketIso = 'us'

type CustomProps = {
  type: InternalTagType
  marketName: string
  onToggleActive: (row: TagRow) => void
  onEdit: (row: TagRow) => void
  onDelete: (row: TagRow) => void
}

type DialogState = { open: boolean; tag?: InternalTag }

type PageConfirmData = { action: 'toggleActive'; row: TagRow } | { action: 'delete'; row: TagRow } | { action: 'discardOrder'; type: InternalTagType }

export const TagsPage: FC = () => {
  usePage(pageService.getInternalPageWithId('internal-tags'))
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { run, notifySuccess, notifyError } = useMutationFeedback()
  const [searchParams, setSearchParams] = useSearchParams()
  const typeParam = searchParams.get('type')
  const type: InternalTagType = isTagType(typeParam) ? typeParam : InternalTagType.Feature

  const marketsQuery = useGetInternalMarketsQuery()
  const markets = useMemo(() => [...(marketsQuery.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)), [marketsQuery.data])
  const requestedMarketIso = searchParams.get('market') ?? defaultMarketIso
  const marketIso = markets.length === 0 || markets.some((market) => market.iso === requestedMarketIso) ? requestedMarketIso : defaultMarketIso
  const marketName = markets.find((market) => market.iso === marketIso)?.name ?? marketIso.toUpperCase()

  const tagsQuery = useGetInternalTagsQuery()
  const conceptsQuery = useGetInternalConceptsQuery()
  const languageMapsQuery = useGetLanguageMapsQuery()
  const featureMapQuery = useGetInternalFeatureMapQuery({ marketIso })
  const implementationsQuery = useGetInternalTagImplementationsQuery({ marketIso })
  const [saveTag, { isLoading: isSaving }] = useSaveInternalTagMutation()
  const [deleteTag] = useDeleteInternalTagMutation()
  const [saveTagOrder, { isLoading: isSavingOrder }] = useSaveInternalTagOrderMutation()

  const [search, setSearch] = useState('')
  const [featureTagFilter, setFeatureTagFilter] = useState(allFeatureTags)
  const [reorderedIds, setReorderedIds] = useState<string[]>()
  const [dialogState, setDialogState] = useState<DialogState>({ open: false })
  const [confirmDialogData, setConfirmDialogData] = useState<ConfirmDialogData<PageConfirmData>>()

  const tags = useMemo(() => tagsQuery.data ?? [], [tagsQuery.data])
  const featureTags = useMemo(() => getFeatureTags(tags), [tags])
  const storedRows = useMemo(
    () =>
      buildTagRows(tags, type, {
        featureMap: featureMapQuery.currentData,
        implementations: implementationsQuery.currentData,
        concepts: conceptsQuery.data,
      }),
    [tags, type, featureMapQuery.currentData, implementationsQuery.currentData, conceptsQuery.data]
  )
  const orderedRows = useMemo(() => {
    if (!reorderedIds) return storedRows
    const rowsById = new Map(storedRows.map((row) => [row.tag.id, row]))
    const reordered = reorderedIds.flatMap((id) => rowsById.get(id) ?? [])
    return [...reordered, ...storedRows.filter((row) => !reorderedIds.includes(row.tag.id))]
  }, [reorderedIds, storedRows])
  const activeFeatureTagFilter = type === InternalTagType.Concept ? featureTagFilter : allFeatureTags
  const rows = useMemo(() => filterTagRows(orderedRows, search, activeFeatureTagFilter), [orderedRows, search, activeFeatureTagFilter])
  const isFiltered = !!search.trim() || activeFeatureTagFilter !== allFeatureTags
  const isLoading = tagsQuery.isLoading || conceptsQuery.isLoading || languageMapsQuery.isLoading
  const isError = tagsQuery.isError || conceptsQuery.isError || languageMapsQuery.isError

  const tableColumns: GRTableColumn<TagRow, CustomProps>[] = useMemo(
    () => [
      {
        labelAccessor: t('internal-service-management:tags_name'),
        accessor: ({ row }) => <Typography fontWeight="bold">{row.tag.name || '---'}</Typography>,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 240 } },
      },
      {
        labelAccessor: t('internal-service-management:tags_description'),
        accessor: ({ row }) => (
          <Box component="span" role="img" aria-label={t(row.tag.description ? 'internal-common:yes' : 'internal-common:no')} display="inline-flex">
            <CheckedStatusCircle value={!!row.tag.description} />
          </Box>
        ),
        headerCellProps: { sx: { width: 110 } },
      },
      {
        labelAccessor: t('internal-service-management:tags_active'),
        accessor: ({ row, customTableProps }) => (
          <Switch
            checked={!!row.tag.active}
            onChange={() => customTableProps?.onToggleActive(row)}
            inputProps={{ 'aria-label': t('internal-service-management:tags_active_toggle', { name: row.tag.name }) }}
          />
        ),
        headerCellProps: { sx: { width: 90 } },
      },
      {
        labelAccessor: t('internal-service-management:tags_features'),
        accessor: ({ row, customTableProps }) => (
          <TagAssignmentCount names={row.assigned} title={t('internal-service-management:tags_features_tooltip', { market: customTableProps?.marketName })} />
        ),
        hidden: ({ customTableProps }) => !customTableProps || !hasFeatureColumn(customTableProps.type),
        headerCellProps: { sx: { width: 110 } },
      },
      {
        labelAccessor: t('internal-service-management:tags_choices'),
        accessor: ({ row, customTableProps }) => (
          <TagAssignmentCount names={row.assigned} title={t('internal-service-management:tags_choices_tooltip', { market: customTableProps?.marketName })} />
        ),
        hidden: ({ customTableProps }) => !customTableProps || !hasChoiceColumn(customTableProps.type),
        headerCellProps: { sx: { width: 110 } },
      },
      {
        labelAccessor: t('internal-service-management:tags_games'),
        accessor: ({ row, customTableProps }) => (
          <TagAssignmentCount names={row.games} title={t('internal-service-management:tags_games_tooltip', { market: customTableProps?.marketName })} />
        ),
        hidden: ({ customTableProps }) => !customTableProps || !hasGamesColumn(customTableProps.type),
        headerCellProps: { sx: { width: 110 } },
      },
      {
        labelAccessor: t('internal-service-management:tags_feature_tag'),
        accessor: ({ row }) => row.featureTagName ?? '–',
        hidden: ({ customTableProps }) => !customTableProps || !hasConceptColumns(customTableProps.type),
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 180 } },
      },
      {
        labelAccessor: t('internal-service-management:tags_screenshots'),
        accessor: ({ row }) => row.screenshotCount ?? '–',
        hidden: ({ customTableProps }) => !customTableProps || !hasConceptColumns(customTableProps.type),
        headerCellProps: { sx: { width: 120 } },
      },
      {
        labelAccessor: '',
        accessor: ({ row, customTableProps }) => (
          <Box display="flex" justifyContent="flex-end">
            {isAssignableTagType(row.tag.type) && (
              <Tooltip title={t('internal-service-management:tags_assign_tag')}>
                <IconButton
                  color="primary"
                  aria-label={t('internal-service-management:tags_assign_tag')}
                  component={RouterLink}
                  to={`/internal/tags/assign?type=${row.tag.type}&tagId=${row.tag.id}`}
                >
                  <PlaylistAddCheck />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={t('internal-service-management:tags_edit')}>
              <IconButton color="primary" aria-label={t('internal-service-management:tags_edit')} onClick={() => customTableProps?.onEdit(row)}>
                <Edit />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('internal-service-management:tags_delete')}>
              <IconButton aria-label={t('internal-service-management:tags_delete')} color="error" onClick={() => customTableProps?.onDelete(row)}>
                <Delete />
              </IconButton>
            </Tooltip>
          </Box>
        ),
        cellProps: { align: 'right' },
        headerCellProps: { sx: { width: 150, minWidth: 150 } },
      },
    ],
    [t]
  )
  const [columns, setColumns] = useState(tableColumns)

  const customProps = useMemo<CustomProps>(
    () => ({
      type,
      marketName,
      onToggleActive: (row) =>
        setConfirmDialogData({
          title: t(row.tag.active ? 'internal-service-management:tags_deactivate' : 'internal-service-management:tags_activate'),
          confirmText: t(row.tag.active ? 'internal-service-management:tags_deactivate_confirm' : 'internal-service-management:tags_activate_confirm', {
            name: row.tag.name,
          }),
          actionText: t(row.tag.active ? 'internal-service-management:tags_deactivate' : 'internal-service-management:tags_activate'),
          destructiveAction: !!row.tag.active,
          data: { action: 'toggleActive', row },
        }),
      onEdit: (row) => setDialogState({ open: true, tag: row.tag }),
      onDelete: (row) =>
        setConfirmDialogData({
          title: t('internal-service-management:tags_delete'),
          confirmText: t('internal-service-management:tags_delete_confirm', { name: row.tag.name }),
          actionText: t('internal-common:delete'),
          destructiveAction: true,
          data: { action: 'delete', row },
        }),
    }),
    [marketName, t, type]
  )

  const fieldNames = useCallback((fields: TagField[]) => fields.map((field) => t(tagFieldLabelKeys[field])).join(', '), [t])

  const changeType = useCallback(
    (nextType: InternalTagType) => {
      setReorderedIds(undefined)
      setSearch('')
      setFeatureTagFilter(allFeatureTags)
      const next = new URLSearchParams(searchParams)
      next.set('type', nextType)
      setSearchParams(next, { replace: true })
    },
    [searchParams, setSearchParams]
  )

  const changeMarket = (iso: string) => {
    const next = new URLSearchParams(searchParams)
    next.set('market', iso)
    setSearchParams(next, { replace: true })
  }

  const handleTypeChange = (nextType: InternalTagType | null) => {
    if (!nextType || nextType === type) return
    if (reorderedIds) {
      setConfirmDialogData({
        title: t('internal-service-management:tags_discard_order'),
        confirmText: t('internal-service-management:tags_discard_order_confirm'),
        actionText: t('internal-service-management:tags_discard_order'),
        destructiveAction: true,
        data: { action: 'discardOrder', type: nextType },
      })
      return
    }
    changeType(nextType)
  }

  const handleSave = useCallback(
    async (body: InternalTagSaveBody) => {
      const isCreate = !body.tag.id
      try {
        const stored = await saveTag(body).unwrap()
        const unstored = getUnstoredTagFields(body.tag, stored)
        if (body.concept) {
          const concepts = await conceptsQuery.refetch().unwrap()
          if (!isConceptStored(concepts, stored.id, body.concept.featureTagId)) unstored.push('featureTag')
        }
        if (unstored.length) {
          notifyError(t('internal-service-management:tags_not_stored', { fields: fieldNames(unstored) }))
          // A retry has to edit the created tag rather than create a second one.
          if (isCreate) {
            await languageMapsQuery.refetch().catch(() => undefined)
            setDialogState({ open: true, tag: stored })
          }
          return
        }
        notifySuccess(t('internal-service-management:tags_saved', { name: stored.name }))
        setDialogState({ open: false })
      } catch {
        notifyError(t('internal-service-management:tags_save_failed'))
      } finally {
        dispatch(invalidateInternalTags())
      }
    },
    [conceptsQuery, dispatch, fieldNames, languageMapsQuery, notifyError, notifySuccess, saveTag, t]
  )

  const toggleActive = useCallback(
    async (tag: InternalTag) => {
      const body = toActiveToggleBody(tag)
      try {
        const stored = await saveTag(body).unwrap()
        const unstored = getUnstoredTagFields(body.tag, stored)
        if (unstored.length) {
          notifyError(t('internal-service-management:tags_not_stored', { fields: fieldNames(unstored) }))
          return
        }
        notifySuccess(t(stored.active ? 'internal-service-management:tags_activated' : 'internal-service-management:tags_deactivated', { name: stored.name }))
      } catch {
        notifyError(t('internal-service-management:tags_save_failed'))
      } finally {
        dispatch(invalidateInternalTags())
      }
    },
    [dispatch, fieldNames, notifyError, notifySuccess, saveTag, t]
  )

  const handleSaveOrder = useCallback(async () => {
    if (!reorderedIds) return
    const saved = await run(saveTagOrder(toTagOrder(orderedRows)).unwrap(), {
      success: t('internal-service-management:tags_order_saved'),
      error: t('internal-service-management:tags_order_save_failed'),
    })
    if (saved.ok) setReorderedIds(undefined)
  }, [orderedRows, reorderedIds, run, saveTagOrder, t])

  const handleConfirm = useCallback(
    async (confirmed?: ConfirmDialogData<PageConfirmData>) => {
      setConfirmDialogData(undefined)
      const data = confirmed?.data
      if (!data) return

      if (data.action === 'discardOrder') {
        changeType(data.type)
        return
      }
      if (data.action === 'toggleActive') {
        await toggleActive(data.row.tag)
        return
      }

      await run(deleteTag(data.row.tag.id).unwrap(), {
        success: t('internal-service-management:tags_deleted', { name: data.row.tag.name }),
        error: t('internal-service-management:tags_delete_failed'),
      })
    },
    [changeType, deleteTag, run, t, toggleActive]
  )

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} mb={2}>
        <Stack direction="row" alignItems="center" gap={2}>
          <TextField
            select
            size="small"
            label={t('internal-common:market')}
            value={markets.length ? marketIso : ''}
            onChange={(event) => changeMarket(event.target.value)}
            sx={{ minWidth: 200 }}
          >
            {markets.map((market) => (
              <MenuItem key={market.iso} value={market.iso}>
                {market.name}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <Stack direction="row" gap={2}>
          <Button
            variant="outlined"
            startIcon={<PlaylistAddCheck />}
            component={RouterLink}
            to={`/internal/tags/assign?type=${isAssignableTagType(type) ? type : InternalTagType.Feature}`}
          >
            {t('internal-service-management:tags_assign')}
          </Button>
          {reorderedIds && (
            <>
              <Button onClick={() => setReorderedIds(undefined)}>{t('internal-service-management:tags_reset_order')}</Button>
              <Button variant="outlined" onClick={handleSaveOrder} disabled={isSavingOrder}>
                {t('internal-service-management:tags_save_order')}
              </Button>
            </>
          )}
          <Button variant="contained" startIcon={<Add />} onClick={() => setDialogState({ open: true })}>
            {t('internal-service-management:tags_add')}
          </Button>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap mb={2}>
        <ToggleButtonGroup
          exclusive
          size="small"
          color="primary"
          value={type}
          onChange={(_, value) => handleTypeChange(value)}
          aria-label={t('internal-service-management:tags_type')}
          sx={{ flexWrap: 'wrap' }}
        >
          {tagTypes.map((tagType) => (
            <ToggleButton key={tagType} value={tagType}>
              {t(tagTypeLabelKeys[tagType])}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <TextField
          size="small"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('internal-service-management:tags_search')}
          inputProps={{ 'aria-label': t('internal-service-management:tags_search') }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 280 }}
        />
        {type === InternalTagType.Concept && (
          <TextField
            select
            size="small"
            label={t('internal-service-management:tags_feature_tag')}
            value={featureTagFilter}
            onChange={(event) => setFeatureTagFilter(event.target.value)}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value={allFeatureTags}>{t('internal-service-management:tags_all_feature_tags')}</MenuItem>
            {featureTags.map((featureTag) => (
              <MenuItem key={featureTag.id} value={featureTag.id}>
                {featureTag.name}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Stack>

      <QueryStateBoundary isLoading={isLoading} isError={isError} errorText={t('internal-service-management:tags_load_failed')}>
        <TableContainer component={Card}>
          <GRTable
            rows={rows}
            columns={columns}
            onColumnsUpdated={setColumns}
            rowIdKey={(row) => row.tag.id}
            customProps={customProps}
            onSortRows={(sortedRows) => setReorderedIds(sortedRows.map((row) => row.tag.id))}
            sortable={!isFiltered}
            disableSortTooltip={t('internal-service-management:tags_reorder_disabled')}
            noRowsLabel={t('internal-common:no_rows')}
            striped
            hoverable
          />
        </TableContainer>
      </QueryStateBoundary>

      <TagEditDialog
        open={dialogState.open}
        tag={dialogState.tag}
        defaultType={type}
        tags={tags}
        concepts={conceptsQuery.data}
        languageMaps={languageMapsQuery.data}
        isSaving={isSaving}
        onClose={() => setDialogState({ open: false })}
        onSave={handleSave}
      />
      <ConfirmDialog open={!!confirmDialogData} confirmDialogData={confirmDialogData} onClose={handleConfirm} />
    </Box>
  )
}
